import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { navLabels } from '@sorare/core/src/lib/glossary';

import { USInfoModal } from '@sorare/us-sports/src/components/USInfoModal';
import { Cards } from '@sorare/us-sports/src/components/gallery/Cards';

import { ClubLayout } from '@sorare/manager/src/components/profile/ClubLayout';
import { myClubFilters } from 'components/searchCards/cardFilters';

import { useLayoutData } from '../layoutData';
import {
  NbaUserCards_deck,
  NbaUserCards_user,
} from './__generated__/page.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 2 })``;

type Props = {
  user: NbaUserCards_user;
  deck?: NbaUserCards_deck;
  isCurrentUser: boolean;
  nbDecks: number;
};

export const UserCards = ({ user, deck, isCurrentUser, nbDecks }: Props) => {
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();

  useEffect(() => {
    if (user) {
      if (deck) {
        return setPageMetadata(deck.name, {
          img: deck?.socialPictureUrls.post,
        });
      }
      return setPageMetadata(formatMessage(navLabels.gallery), {
        img: user.nbaUserProfile?.gallerySocialPictureUrls.post,
      });
    }
    return () => {};
  }, [deck, formatMessage, setPageMetadata, user]);

  const showDecksFilter = isCurrentUser || nbDecks > 0;
  const galleryFilters = myClubFilters(showDecksFilter);

  return (
    <ClubLayout cards>
      <PageHeader text={navLabels.gallery} />
      <Wrapper>
        <Cards
          sport={Sport.NBA}
          user={user}
          galleryFilters={galleryFilters}
          PlayerDetailsComponent={USInfoModal}
        />
      </Wrapper>
    </ClubLayout>
  );
};

UserCards.fragments = {
  user: gql`
    fragment NbaUserCards_user on User {
      slug
      nbaUserProfile {
        id
        gallerySocialPictureUrls {
          post
        }
      }
      ...Cards_user
    }
    ${Cards.fragments.user}
  ` as TypedDocumentNode<NbaUserCards_user>,
  deck: gql`
    fragment NbaUserCards_deck on Deck {
      slug
      name
      socialPictureUrls {
        post
      }
    }
  ` as TypedDocumentNode<NbaUserCards_deck>,
};

const CardsPage = () => {
  const { currentUser } = useCurrentUserContext();
  const { user, initialDeckName, currentDeckName } = useLayoutData();
  const isCurrentUser = user && user.slug === currentUser?.slug;
  return (
    <UserCards
      user={user}
      deck={
        currentDeckName === initialDeckName
          ? user.nbaUserProfile?.deck || undefined
          : undefined
      }
      isCurrentUser={isCurrentUser}
      nbDecks={user?.nbaUserProfile?.decks?.nodes.length || 0}
    />
  );
};

export default CardsPage;
